import { isEqual } from 'lodash';

export interface IncomingPatchResult {
  /** Field values to merge into the form store. A key mapped to `undefined`
   *  means the server dropped the field (cleared / removed). */
  patch: Record<string, unknown>;
  /** Fields held back because the user has unsaved local edits to them. */
  skipped: string[];
}

/**
 * Build the per-field patch to apply from an incoming server snapshot.
 *
 * Replaces the old all-or-nothing gate (one global dirty flag dropping the
 * ENTIRE update) with field granularity: every incoming field is applied
 * except the ones the user has edited locally and not yet saved. Those keep
 * the local value; the pending save will push them, and the save echo will
 * reconcile.
 */
export function buildIncomingPatch(
  serverFormData: Record<string, unknown>,
  currentFormData: Record<string, unknown>,
  dirtyFields: ReadonlySet<string>,
): IncomingPatchResult {
  const patch: Record<string, unknown> = {};
  const skipped: string[] = [];

  const keys = new Set([...Object.keys(serverFormData), ...Object.keys(currentFormData)]);
  for (const key of keys) {
    const incoming = serverFormData[key];
    if (isEqual(incoming, currentFormData[key])) continue;
    if (dirtyFields.has(key)) {
      skipped.push(key);
      continue;
    }
    patch[key] = incoming;
  }

  return { patch, skipped };
}
